import { useState } from 'react';
import { z } from 'zod';
import {
  FORMULA_PARAM_UNIT_LABELS,
  MEASUREMENT_UNIT_LABELS,
  MeasurementUnitSchema,
  formulaParamLabel,
  type FormulaParameterDef,
  type MeasurementUnit,
  type QuantityFormulaDto,
} from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { useCreateQuantity, usePreviewQuantity, useQuantityFormulas } from './hooks';

type Mode = 'STRUCTURED_FORMULA' | 'DIRECT_QUANTITY';

interface RectangleRow {
  length: string;
  width: string;
}

type ParamValue = string | string[] | RectangleRow[];

const directSchema = z.object({
  directQuantity: z
    .string()
    .regex(/^\d+(\.\d{1,3})?$/, 'Quantité invalide (nombre positif, 3 décimales maximum).'),
  measurementUnit: MeasurementUnitSchema,
  source: z.string().min(1, 'La source est requise.'),
  justification: z.string().min(1, 'La justification est requise.'),
});

function normalizeDecimal(v: string): string {
  return v.trim().replace(',', '.');
}

function emptyValueFor(def: FormulaParameterDef): ParamValue {
  if (def.kind === 'DECIMAL_LIST') return [''];
  if (def.kind === 'RECTANGLE_LIST') return [{ length: '', width: '' }];
  return '';
}

function initialParams(formula: QuantityFormulaDto): Record<string, ParamValue> {
  const values: Record<string, ParamValue> = {};
  for (const def of formula.parametersSchema?.parameters ?? []) {
    values[def.name] = emptyValueFor(def);
  }
  return values;
}

/**
 * Builds the wire `parameters` object from the dynamic form state, enforcing the
 * declarative `parametersSchema` (required/non-empty). Values stay strings — the
 * SERVER parses and computes; the web performs no formula math whatsoever.
 */
function buildParameters(
  defs: FormulaParameterDef[],
  values: Record<string, ParamValue>,
): { parameters?: Record<string, unknown>; errors: Record<string, string> } {
  const parameters: Record<string, unknown> = {};
  const errors: Record<string, string> = {};

  for (const def of defs) {
    const required = def.required !== false;
    const raw = values[def.name];

    if (def.kind === 'DECIMAL' || def.kind === 'INT') {
      const v = typeof raw === 'string' ? normalizeDecimal(raw) : '';
      if (!v) {
        if (required) errors[def.name] = 'Ce paramètre est requis.';
        continue;
      }
      parameters[def.name] = v;
    } else if (def.kind === 'DECIMAL_LIST') {
      const list = Array.isArray(raw) ? (raw as string[]) : [];
      const cleaned = list.map(normalizeDecimal).filter((v) => v.length > 0);
      if (cleaned.length === 0) {
        if (required) errors[def.name] = 'Veuillez saisir au moins une valeur.';
        continue;
      }
      parameters[def.name] = cleaned;
    } else {
      const rows = Array.isArray(raw) ? (raw as RectangleRow[]) : [];
      const cleaned = rows
        .map((r) => ({ length: normalizeDecimal(r.length), width: normalizeDecimal(r.width) }))
        .filter((r) => r.length.length > 0 || r.width.length > 0);
      if (cleaned.length === 0) {
        if (required) errors[def.name] = 'Veuillez saisir au moins un rectangle.';
        continue;
      }
      if (cleaned.some((r) => !r.length || !r.width)) {
        errors[def.name] = 'Chaque rectangle doit avoir une longueur et une largeur.';
        continue;
      }
      parameters[def.name] = cleaned;
    }
  }

  return { parameters: Object.keys(errors).length === 0 ? parameters : undefined, errors };
}

function paramFieldLabel(def: FormulaParameterDef): string {
  const unit = def.unit ? FORMULA_PARAM_UNIT_LABELS[def.unit] : undefined;
  return unit ? `${formulaParamLabel(def.name)} (${unit})` : formulaParamLabel(def.name);
}

export function QuantityDialog({
  elementId,
  open,
  onClose,
}: {
  elementId: string;
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  if (!open) return null;
  return (
    <Dialog open onClose={onClose} title="Ajouter une quantité" className="max-w-xl">
      <QuantityDialogBody elementId={elementId} onClose={onClose} />
    </Dialog>
  );
}

/** Mounted fresh on each dialog opening so the form state never leaks between uses. */
function QuantityDialogBody({
  elementId,
  onClose,
}: {
  elementId: string;
  onClose: () => void;
}): JSX.Element {
  const [mode, setMode] = useState<Mode>('STRUCTURED_FORMULA');

  // Structured mode state.
  const formulasQuery = useQuantityFormulas(mode === 'STRUCTURED_FORMULA');
  const [formulaId, setFormulaId] = useState('');
  const [paramValues, setParamValues] = useState<Record<string, ParamValue>>({});
  const [paramErrors, setParamErrors] = useState<Record<string, string>>({});

  // Direct mode state.
  const [directQuantity, setDirectQuantity] = useState('');
  const [directUnit, setDirectUnit] = useState<MeasurementUnit>('M2');
  const [source, setSource] = useState('');
  const [justification, setJustification] = useState('');
  const [directErrors, setDirectErrors] = useState<Record<string, string>>({});

  const createQuantity = useCreateQuantity(elementId);
  const preview = usePreviewQuantity();

  const formulas = formulasQuery.data ?? [];
  const formula = formulas.find((f) => f.id === formulaId);
  const schema = formula?.parametersSchema ?? null;

  const selectFormula = (id: string) => {
    setFormulaId(id);
    setParamErrors({});
    preview.reset();
    const next = formulas.find((f) => f.id === id);
    setParamValues(next ? initialParams(next) : {});
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setParamErrors({});
    setDirectErrors({});
    preview.reset();
    createQuantity.reset();
  };

  const setScalar = (name: string, value: string) =>
    setParamValues((prev) => ({ ...prev, [name]: value }));

  const setListItem = (name: string, index: number, value: string) =>
    setParamValues((prev) => {
      const list = [...((prev[name] as string[]) ?? [])];
      list[index] = value;
      return { ...prev, [name]: list };
    });

  const setRect = (name: string, index: number, field: keyof RectangleRow, value: string) =>
    setParamValues((prev) => {
      const rows = [...((prev[name] as RectangleRow[]) ?? [])];
      const row = rows[index] ?? { length: '', width: '' };
      rows[index] = { ...row, [field]: value };
      return { ...prev, [name]: rows };
    });

  const addRow = (def: FormulaParameterDef) =>
    setParamValues((prev) => {
      const current = prev[def.name];
      if (def.kind === 'DECIMAL_LIST') {
        return { ...prev, [def.name]: [...((current as string[]) ?? []), ''] };
      }
      return {
        ...prev,
        [def.name]: [...((current as RectangleRow[]) ?? []), { length: '', width: '' }],
      };
    });

  const removeRow = (name: string, index: number) =>
    setParamValues((prev) => {
      const list = [...((prev[name] as unknown[]) ?? [])];
      list.splice(index, 1);
      return { ...prev, [name]: list as ParamValue };
    });

  const buildStructuredPayload = () => {
    if (!formula || !schema) return null;
    const { parameters, errors } = buildParameters(schema.parameters, paramValues);
    setParamErrors(errors);
    if (!parameters) return null;
    return { formulaCode: formula.code, parameters, measurementUnit: schema.outputUnit };
  };

  const onPreview = () => {
    const payload = buildStructuredPayload();
    if (!payload) return;
    preview.mutate({ inputMode: 'STRUCTURED_FORMULA', ...payload });
  };

  const onSubmit = () => {
    if (mode === 'STRUCTURED_FORMULA') {
      if (!formula) {
        setParamErrors({ __formula: 'Veuillez choisir une formule.' });
        return;
      }
      const payload = buildStructuredPayload();
      if (!payload) return;
      createQuantity.mutate(
        { inputMode: 'STRUCTURED_FORMULA', ...payload },
        { onSuccess: onClose },
      );
      return;
    }

    const parsed = directSchema.safeParse({
      directQuantity: normalizeDecimal(directQuantity),
      measurementUnit: directUnit,
      source: source.trim(),
      justification: justification.trim(),
    });
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '');
        if (key && !errors[key]) errors[key] = issue.message;
      }
      setDirectErrors(errors);
      return;
    }
    setDirectErrors({});
    createQuantity.mutate({ inputMode: 'DIRECT_QUANTITY', ...parsed.data }, { onSuccess: onClose });
  };

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant={mode === 'STRUCTURED_FORMULA' ? 'primary' : 'outline'}
          aria-pressed={mode === 'STRUCTURED_FORMULA'}
          onClick={() => switchMode('STRUCTURED_FORMULA')}
        >
          Formule structurée
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === 'DIRECT_QUANTITY' ? 'primary' : 'outline'}
          aria-pressed={mode === 'DIRECT_QUANTITY'}
          onClick={() => switchMode('DIRECT_QUANTITY')}
        >
          Quantité directe
        </Button>
      </div>

      {createQuantity.isError ? (
        <Alert variant="error" details={toDisplayError(createQuantity.error).details}>
          {toDisplayError(createQuantity.error).message}
        </Alert>
      ) : null}

      {mode === 'STRUCTURED_FORMULA' ? (
        <div className="space-y-4">
          {formulasQuery.isLoading ? (
            <Spinner label="Chargement des formules…" />
          ) : formulasQuery.isError ? (
            <Alert variant="error">{toDisplayError(formulasQuery.error).message}</Alert>
          ) : (
            <div>
              <Label htmlFor="quantity-formula">Formule</Label>
              <Select
                id="quantity-formula"
                value={formulaId}
                onChange={(e) => selectFormula(e.target.value)}
              >
                <option value="">— Choisir une formule —</option>
                {formulas.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label} (v{f.version})
                  </option>
                ))}
              </Select>
              {paramErrors.__formula ? (
                <p className="mt-1 text-sm text-red-600">{paramErrors.__formula}</p>
              ) : null}
              {formula?.description ? (
                <p className="mt-1 text-xs text-slate-500">{formula.description}</p>
              ) : null}
            </div>
          )}

          {formula && !schema ? (
            <Alert variant="warning">
              Schéma de paramètres indisponible pour cette formule — saisie impossible.
            </Alert>
          ) : null}

          {formula && schema ? (
            <>
              {schema.parameters.map((def) => (
                <ParamField
                  key={def.name}
                  def={def}
                  value={paramValues[def.name] ?? emptyValueFor(def)}
                  error={paramErrors[def.name]}
                  onScalarChange={(v) => setScalar(def.name, v)}
                  onListItemChange={(i, v) => setListItem(def.name, i, v)}
                  onRectChange={(i, field, v) => setRect(def.name, i, field, v)}
                  onAddRow={() => addRow(def)}
                  onRemoveRow={(i) => removeRow(def.name, i)}
                />
              ))}

              <p className="text-sm text-slate-600">
                Unité de mesure :{' '}
                <span className="font-medium">{MEASUREMENT_UNIT_LABELS[schema.outputUnit]}</span>{' '}
                (déterminée par la formule)
              </p>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onPreview}
                  loading={preview.isPending}
                >
                  Prévisualiser
                </Button>
                {preview.isError ? (
                  <span className="text-sm text-red-600">
                    {toDisplayError(preview.error).message}
                  </span>
                ) : null}
              </div>
              {preview.data ? (
                <Alert variant="success">
                  Quantité calculée par le serveur : {preview.data.calculatedQuantity}{' '}
                  {MEASUREMENT_UNIT_LABELS[preview.data.measurementUnit]}
                </Alert>
              ) : null}
            </>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <Alert variant="info">
            Une quantité directe doit être justifiée : indiquez la source (plan, métré, document) et
            la justification de la valeur saisie.
          </Alert>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="direct-quantity">Quantité</Label>
              <Input
                id="direct-quantity"
                inputMode="decimal"
                aria-invalid={Boolean(directErrors.directQuantity)}
                value={directQuantity}
                onChange={(e) => setDirectQuantity(e.target.value)}
              />
              {directErrors.directQuantity ? (
                <p className="mt-1 text-sm text-red-600">{directErrors.directQuantity}</p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="direct-unit">Unité de mesure</Label>
              <Select
                id="direct-unit"
                value={directUnit}
                onChange={(e) => setDirectUnit(e.target.value as MeasurementUnit)}
              >
                {MeasurementUnitSchema.options.map((u) => (
                  <option key={u} value={u}>
                    {MEASUREMENT_UNIT_LABELS[u]}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="direct-source">Source</Label>
            <Input
              id="direct-source"
              aria-invalid={Boolean(directErrors.source)}
              value={source}
              onChange={(e) => setSource(e.target.value)}
            />
            {directErrors.source ? (
              <p className="mt-1 text-sm text-red-600">{directErrors.source}</p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="direct-justification">Justification</Label>
            <textarea
              id="direct-justification"
              rows={3}
              aria-invalid={Boolean(directErrors.justification)}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
            />
            {directErrors.justification ? (
              <p className="mt-1 text-sm text-red-600">{directErrors.justification}</p>
            ) : null}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={createQuantity.isPending}
        >
          Annuler
        </Button>
        <Button type="button" onClick={onSubmit} loading={createQuantity.isPending}>
          Enregistrer la quantité
        </Button>
      </div>
    </div>
  );
}

/** One dynamic field driven by a `parametersSchema` parameter descriptor. */
function ParamField({
  def,
  value,
  error,
  onScalarChange,
  onListItemChange,
  onRectChange,
  onAddRow,
  onRemoveRow,
}: {
  def: FormulaParameterDef;
  value: ParamValue;
  error?: string;
  onScalarChange: (value: string) => void;
  onListItemChange: (index: number, value: string) => void;
  onRectChange: (index: number, field: keyof RectangleRow, value: string) => void;
  onAddRow: () => void;
  onRemoveRow: (index: number) => void;
}): JSX.Element {
  const label = paramFieldLabel(def);

  if (def.kind === 'DECIMAL' || def.kind === 'INT') {
    const id = `param-${def.name}`;
    return (
      <div>
        <Label htmlFor={id}>{label}</Label>
        <Input
          id={id}
          inputMode={def.kind === 'INT' ? 'numeric' : 'decimal'}
          aria-invalid={Boolean(error)}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onScalarChange(e.target.value)}
        />
        {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
      </div>
    );
  }

  if (def.kind === 'DECIMAL_LIST') {
    const list = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div>
        <Label>{label}</Label>
        <div className="space-y-2">
          {list.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                aria-label={`${label} — valeur ${i + 1}`}
                inputMode="decimal"
                value={item}
                onChange={(e) => onListItemChange(i, e.target.value)}
              />
              {list.length > 1 ? (
                <Button type="button" size="sm" variant="ghost" onClick={() => onRemoveRow(i)}>
                  Retirer
                </Button>
              ) : null}
            </div>
          ))}
        </div>
        <Button type="button" size="sm" variant="outline" className="mt-2" onClick={onAddRow}>
          Ajouter une valeur
        </Button>
        {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
      </div>
    );
  }

  const rows = Array.isArray(value) ? (value as RectangleRow[]) : [];
  return (
    <div>
      <Label>{label}</Label>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Input
              className="w-32 flex-1"
              aria-label={`${label} ${i + 1} — longueur (m)`}
              inputMode="decimal"
              placeholder="Longueur"
              value={row.length}
              onChange={(e) => onRectChange(i, 'length', e.target.value)}
            />
            <span className="text-slate-400">×</span>
            <Input
              className="w-32 flex-1"
              aria-label={`${label} ${i + 1} — largeur (m)`}
              inputMode="decimal"
              placeholder="Largeur"
              value={row.width}
              onChange={(e) => onRectChange(i, 'width', e.target.value)}
            />
            {rows.length > 1 ? (
              <Button type="button" size="sm" variant="ghost" onClick={() => onRemoveRow(i)}>
                Retirer
              </Button>
            ) : null}
          </div>
        ))}
      </div>
      <Button type="button" size="sm" variant="outline" className="mt-2" onClick={onAddRow}>
        Ajouter un rectangle
      </Button>
      {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
    </div>
  );
}
